import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr, inrCompact, num } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { ChartCard, Bars } from "@/components/Charts";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MapPin, Plus, Power } from "lucide-react";
import { toast } from "sonner";

export default function Locations() {
  const [locs, setLocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState({});
  const load = () => api.get("/locations").then(({ data }) => setLocs(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.city || !f.state) { toast.error("City and State required"); return; }
    try {
      if (edit) await api.put(`/locations/${edit.id}`, f);
      else await api.post("/locations", f);
      toast.success("Location saved"); setOpen(false); setF({}); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggle = async (l) => {
    try { await api.put(`/locations/${l.id}/status`, { status: l.status === "Active" ? "Inactive" : "Active" }); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const openEdit = (l) => { setEdit(l); setF({ city: l.city, state: l.state }); setOpen(true); };

  return (
    <div>
      <PageHeader title="Location Management" subtitle="Manage cities & states used across onboarding, orders, reports & filters">
        <Button onClick={() => { setEdit(null); setF({}); setOpen(true); }} data-testid="add-location-btn"><Plus className="mr-1 h-4 w-4" /> Add Location</Button>
      </PageHeader>

      <Tabs defaultValue="manage">
        <TabsList><TabsTrigger value="manage" data-testid="loc-tab-manage">Manage</TabsTrigger><TabsTrigger value="performance" data-testid="loc-tab-performance">Performance</TabsTrigger></TabsList>

        <TabsContent value="manage" className="mt-4">
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>City</TableHead><TableHead>State</TableHead><TableHead className="text-right">DPs</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {locs.map((l) => (
                  <TableRow key={l.id || l.name} data-testid={`location-row-${l.city}`}>
                    <TableCell className="font-semibold">{l.city || l.name}</TableCell>
                    <TableCell>{l.state}</TableCell>
                    <TableCell className="text-right tabular">{num(l.dp_count)}</TableCell>
                    <TableCell><StatusBadge status={l.status || "Active"} /></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(l)} data-testid={`edit-location-${l.city}`}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => toggle(l)} data-testid={`toggle-location-${l.city}`} title="Activate/Deactivate"><Power className={`h-4 w-4 ${l.status === "Active" ? "text-emerald-500" : "text-rose-500"}`} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="mt-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title="Orders by Location"><Bars data={locs.map((l) => ({ location: l.city || l.name, orders: l.orders }))} x="location" y="orders" color="#2563EB" /></ChartCard>
            <ChartCard title="Earnings by Location"><Bars data={locs.map((l) => ({ location: l.city || l.name, earnings: l.earnings }))} x="location" y="earnings" color="#10B981" /></ChartCard>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {locs.map((l) => (
              <div key={l.id || l.name} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /><h3 className="font-head font-semibold">{l.city || l.name}, {l.state}</h3></div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <KV k="DP Count" v={num(l.dp_count)} /><KV k="Active DP" v={num(l.active_dp)} />
                  <KV k="Orders" v={num(l.orders)} /><KV k="Earnings" v={inrCompact(l.earnings)} />
                  <KV k="Avg Orders/DP" v={l.avg_orders_dp} />
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card" data-testid="location-dialog">
          <DialogHeader><DialogTitle className="font-head">{edit ? "Edit" : "Add"} Location</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>City</Label><Input value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="location-city" placeholder="Faridabad" /></div>
            <div><Label>State</Label><Input value={f.state || ""} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="location-state" placeholder="Haryana" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-location">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const KV = ({ k, v }) => (<div className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="tabular font-medium">{v}</span></div>);
